import { generate } from "@ant-design/colors";
import { theme } from "antd";
import { CSSProperties, PropsWithChildren } from "react";
import { mono } from "src/fonts";
import { useColorScheme } from "src/hooks/useColorScheme";

export function CssVars(
  props: PropsWithChildren<{ className?: string; style?: CSSProperties }>
) {
  const { token } = theme.useToken();
  const colorScheme = useColorScheme();
  const neutral = generate("#bfbfbf", {
    theme: colorScheme === "dark" ? "dark" : "default",
  });
  const banner = generate("#4b3f72", {
    theme: colorScheme === "dark" ? "dark" : "default",
  });
  const purple = generate("#722ed1", {
    theme: colorScheme === "dark" ? "dark" : "default",
  });

  // Shadow system
  const shadowOpacity = colorScheme === "dark" ? "0.4" : "0.12";
  const shadowColor = colorScheme === "dark" ? "0, 0, 0" : "17, 25, 39";
  const shadow = {
    xs: `0 1px 2px rgba(${shadowColor}, ${shadowOpacity})`,
    sm: `0 2px 4px rgba(${shadowColor}, ${shadowOpacity})`,
    md: `0 4px 8px rgba(${shadowColor}, ${shadowOpacity})`,
    lg: `0 8px 16px rgba(${shadowColor}, ${shadowOpacity})`,
    xl: `0 16px 24px rgba(${shadowColor}, ${shadowOpacity})`,
    inner: `inset 0 1px 2px rgba(${shadowColor}, ${shadowOpacity})`,
  };

  // Overlay system
  const overlayOpacity = colorScheme === "dark" ? "0.8" : "0.95";
  const overlayColor =
    colorScheme === "dark" ? token.colorBgContainer : "#ffffff";

  const overlay = {
    color:
      colorScheme === "dark"
        ? "rgba(0, 0, 0, 0.6)"
        : "rgba(255, 255, 255, 0.8)",
    opacity: colorScheme === "dark" ? 0.8 : 0.95,
  };

  return (
    <div
      className={props.className}
      style={
        {
          height: "100%",
          color: token.colorText,
          fontSize: 14,
          ...props.style,
          ["--font-mono"]: mono.style.fontFamily,
          ["--color-scheme"]: colorScheme,

          // Color system
          ["--color-primary"]: token.colorPrimary,
          ["--color-primary-hover"]: token.colorPrimaryHover,
          ["--color-primary-active"]: token.colorPrimaryActive,
          ["--color-primary-text"]: token.colorPrimaryText,
          ["--color-primary-text-hover"]: token.colorPrimaryTextHover,
          ["--color-primary-bg"]: token.colorPrimaryBg,
          ["--color-primary-bg-hover"]: token.colorPrimaryBgHover,

          // Link colors
          ["--color-link"]: token.colorLink,
          ["--color-link-hover"]: token.colorLinkHover,
          ["--color-link-active"]: token.colorLinkActive,

          // Selectable colors
          ["--color-selectable-hover"]: `${token.colorPrimary}11`,
          ["--color-selectable-highlight-bg"]: token.colorBgTextHover,
          ["--color-selectable-highlight-fg"]: token.colorTextSecondary,
          ["--color-highlight"]: token.colorHighlight,

          // Text colors
          ["--color-text"]: token.colorText,
          ["--color-text-secondary"]: token.colorTextSecondary,
          ["--color-text-tertiary"]: token.colorTextTertiary,
          ["--color-text-quaternary"]: token.colorTextQuaternary,
          ["--color-text-disabled"]: token.colorTextDisabled,
          ["--color-text-inverse"]:
            colorScheme === "dark" ? token.colorText : "#ffffff",
          ["--color-text-inverse-secondary"]:
            colorScheme === "dark"
              ? token.colorTextSecondary
              : "rgba(255, 255, 255, 0.85)",
          ["--color-text-inverse-tertiary"]:
            colorScheme === "dark"
              ? token.colorTextTertiary
              : "rgba(255, 255, 255, 0.65)",
          ["--color-text-description"]: token.colorTextDescription,
          ["--color-text-light-solid"]: token.colorTextLightSolid,
          ["--color-text-placeholder"]: token.colorTextPlaceholder,
          ["--color-text-vibrant-accent"]:
            colorScheme === "dark" ? token.colorText : "#662e46",

          // Icon colors
          ["--color-icon"]: token.colorIcon,
          ["--color-icon-hover"]: token.colorIconHover,

          // Status colors
          ["--color-success"]: token.colorSuccess,
          ["--color-success-text"]: token.colorSuccessText,
          ["--color-success-bg"]: token.colorSuccessBg,
          ["--color-success-border"]: token.colorSuccessBorder,
          ["--color-warning"]: token.colorWarning,
          ["--color-warning-text"]: token.colorWarningText,
          ["--color-warning-bg"]: token.colorWarningBg,
          ["--color-warning-border"]: token.colorWarningBorder,
          ["--color-error"]: token.colorError,
          ["--color-error-text"]: token.colorErrorText,
          ["--color-error-bg"]:
            colorScheme === "dark" ? "#ff647240" : token.colorErrorBg,
          ["--color-error-border"]: token.colorErrorBorder,
          ["--color-info"]: token.colorInfo,
          ["--color-info-text"]: token.colorInfoText,
          ["--color-info-bg"]: token.colorInfoBg,
          ["--color-info-border"]: token.colorInfoBorder,

          // Background colors
          ["--color-bg-base"]: token.colorBgBase,
          ["--color-bg-container"]:
            colorScheme === "dark" ? "#262a3b" : token.colorBgContainer,
          ["--color-bg-elevated"]: token.colorBgElevated,
          ["--color-bg-layout"]: token.colorBgLayout,
          ["--color-bg-spotlight"]: token.colorBgSpotlight,
          ["--color-bg-mask"]: `rgba(0, 0, 0, ${
            colorScheme === "dark" ? "0.65" : "0.45"
          })`,
          ["--color-bg-base-2"]: colorScheme === "dark" ? "#161f27" : "#E0DEDC",
          ["--color-bg-base-3"]: "#262a3b",

          // Border colors
          ["--color-border"]: token.colorBorder,
          ["--color-border-secondary"]: token.colorBorderSecondary,
          ["--color-split"]: token.colorSplit,

          // Shadow system
          ["--shadow-xs"]: shadow.xs,
          ["--shadow-sm"]: shadow.sm,
          ["--shadow-md"]: shadow.md,
          ["--shadow-lg"]: shadow.lg,
          ["--shadow-xl"]: shadow.xl,
          ["--shadow-inner"]: shadow.inner,

          //border radius
          ["--border-radius"]: token.borderRadius,
          ["--border-radius-md"]: "12px",
          ["--border-radius-lg"]: token.borderRadiusLG,
          ["--border-radius-sm"]: token.borderRadiusSM,
          ["--border-radius-xs"]: token.borderRadiusXS,

          // Overlay system
          ["--overlay-color"]: overlay.color,
          ["--overlay-opacity"]: overlay.opacity,

          // Card system
          ["--card-shadow"]: shadow.sm,
          ["--card-shadow-hover"]: shadow.md,
          ["--card-bg"]: token.colorBgContainer,
          ["--card-bg-hover"]:
            colorScheme === "dark"
              ? token.colorBgElevated
              : token.colorBgContainer,

          // Pill colors
          ["--color-pill-blue"]:
            colorScheme === "dark" ? "#6fa9fac4" : "#cae0ffc4",
          ["--color-pill-blue-accent"]:
            colorScheme === "dark" ? "#00265b" : "#217dff",
          ["--color-pill-green"]:
            colorScheme === "dark" ? "#69a762f0" : "#c6ffbf3b",
          ["--color-pill-green-accent"]:
            colorScheme === "dark" ? "#093103" : "#2c8121",
          ["--color-pill-yellow"]: "#ff9800",
          ["--color-pill-yellow-accent"]: "#ff9800",
          ["--color-pill-red"]:
            colorScheme === "dark" ? "#ff899d40" : "#fff9fa",
          ["--color-pill-red-accent"]:
            colorScheme === "dark" ? "#ff002c" : "#f43f5e",
          ["--color-pill-purple"]:
            colorScheme === "dark" ? "#ceabfff0" : "#ceabff24",
          ["--color-pill-purple-accent"]:
            colorScheme === "dark" ? "#5712bb" : "#6429b8cc",
          ["--color-pill-orange"]: "#fa8c16",
          ["--color-pill-orange-accent"]: "#fa8c16",
          ["--color-pill-gray"]: "#8c8c8c",
          ["--color-pill-gray-accent"]: "#8c8c8c",
          ["--color-pill-brown"]: "#a5673f",
          ["--color-pill-brown-accent"]: "#a5673f",
          ["--color-pill-pink"]: "#ffeff8",
          ["--color-pill-pink-accent"]: "#eb2f96",

          //-----------------------------------------------------------------
          //new design system variables to start fresh
          ["--border-radius-1"]: "4px",
          ["--border-radius-2"]: "8px",
          ["--border-radius-3"]: "12px",
          ["--border-radius-4"]: "16px",
          ["--border-radius-5"]: "20px",
          ["--border-radius-6"]: "24px",

          //colors
          ["--color-foreground-primary"]:
            colorScheme === "dark" ? "#ffffffF5" : "#000000F5",
          ["--color-foreground-secondary"]:
            colorScheme === "dark" ? "#ffffffE0" : "#000000E0",
          ["--color-foreground-tertiary"]:
            colorScheme === "dark" ? "#ffffffba" : "#0000008F",
          ["--color-foreground-quaternary"]:
            colorScheme === "dark" ? "#ffffff3d" : "#0000003d",

          ["--color-background-primary"]:
            colorScheme === "dark" ? "#ffffff05" : "#00000005",

          ["--color-background-tertiary"]:
            colorScheme === "dark" ? "#383b4b" : "#f4f7fa",

          ["--color-background-quaternary"]:
            colorScheme === "dark" ? "#383b4b" : "#f9f7f7f5",

          ["--color-text-9"]: colorScheme === "dark" ? "#ffffff" : "#2D2E32",

          ["--color-alto-1"]: colorScheme === "dark" ? "#df9c6091" : "#EBEAE9",
          ["--color-alto-2"]: "#E0DEDC",

          ["--color-blue-1"]: colorScheme === "dark" ? "#0000ff2e" : "#EBECF2",
          ["--color-blue-2"]: "#404664",

          ["--color-ebony-1"]: colorScheme === "dark" ? "#29576f" : "#EBECF3",
          ["--color-ebony-2"]: "#D3D6E4",

          ["--color-tapestry-1"]:
            colorScheme === "dark" ? "#ff61b970" : "#F6EDF2",
          ["--color-tapestry-2"]: "#EEDCE6",

          ["--color-yellow-1"]:
            colorScheme === "dark" ? "#fff10075" : "#F7F3E8",
          ["--color-green-1"]: colorScheme === "dark" ? "#a2ff7a57" : "#EFF6EC",
          ["--color-sky-1"]: colorScheme === "dark" ? "#58d5f291" : "#E9F6F9",

          //simple padding
          ["--padding-0"]: "2px",
          ["--padding-1"]: "4px",
          ["--padding-2"]: "8px",
          ["--padding-3"]: "12px",
          ["--padding-4"]: "16px",
          ["--padding-5"]: "24px",
          ["--padding-6"]: "32px",
          ["--padding-7"]: "40px",

          //line height
          ["--line-height-1"]: "1",
          ["--line-height-2"]: "1.15",
          ["--line-height-3"]: "1.25",
          ["--line-height-4"]: "1.5",
          ["--line-height-5"]: "1.75",
          ["--line-height-6"]: "2",

          //font size
          ["--font-size-1"]: "10px",
          ["--font-size-2"]: "12px",
          ["--font-size-3"]: "14px",
          ["--font-size-4"]: "16px",
          ["--font-size-5"]: "18px",
          ["--font-size-6"]: "20px",

          //font weight
          ["--font-weight-1"]: colorScheme === "dark" ? "500" : "400",
          ["--font-weight-2"]: colorScheme === "dark" ? "600" : "500",
          ["--font-weight-3"]: colorScheme === "dark" ? "700" : "600",
          ["--font-weight-4"]: colorScheme === "dark" ? "800" : "700",
          ["--font-weight-5"]: colorScheme === "dark" ? "900" : "800",
          ["--font-weight-6"]: colorScheme === "dark" ? "1000" : "900",

          //font family
          ["--font-family-core"]: "Inter, sans-serif",
          ["--font-family-accent"]: "Monaco, monospace",

          //letter spacing
          ["--letter-spacing-1"]: "0.05em",
          ["--letter-spacing-2"]: "0.05em",
        } as CSSProperties
      }
    >
      {props.children}
    </div>
  );
}
